import { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';
import ScrollToTopButton from '../../components/feature/ScrollToTopButton';
import BlogHero from './components/BlogHero';
import BlogFeaturedPost from './components/BlogFeaturedPost';
import BlogGrid from './components/BlogGrid';
import { blogPosts, categoryCounts } from '../../mocks/blogPosts';

const SCHEMA_BLOG = JSON.stringify({
  '@context': 'https://schema.org',
  '@type': 'Blog',
  name: 'Blog Técnico Engeporto - AVCB, CLCB e Segurança do Trabalho',
  description:
    'Artigos especializados sobre AVCB, CLCB, segurança do trabalho e perícias técnicas nas regiões de Sorocaba e Campinas.',
  url: `${import.meta.env.VITE_SITE_URL}/blog`,
  publisher: {
    '@type': 'Organization',
    name: 'Engeporto Engenharia e Serviços Ltda',
    url: import.meta.env.VITE_SITE_URL,
  },
  blogPost: blogPosts.map((p) => ({
    '@type': 'BlogPosting',
    headline: p.title,
    description: p.excerpt,
    datePublished: p.dateISO,
    url: `${import.meta.env.VITE_SITE_URL}/blog/${p.slug}`,
    keywords: p.tags.join(', '),
  })),
});

const categories = [
  { label: 'Todos', value: 'Todos' },
  { label: 'Prevenção de Incêndio', value: 'Prevenção de Incêndio' },
  { label: 'Segurança do Trabalho', value: 'Segurança do Trabalho' },
  { label: 'Avaliações e Perícias', value: 'Avaliações e Perícias' },
];

const BlogPage = () => {
  const [selectedCategory, setSelectedCategory] = useState('Todos');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    document.title =
      'Blog Técnico Engeporto Sorocaba Campinas - AVCB, CLCB e Segurança do Trabalho';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute(
        'content',
        'Blog técnico da Engeporto com artigos sobre AVCB, CLCB, PGR, LTCAT, perícias e segurança do trabalho nas regiões de Sorocaba e Campinas. Conteúdo especializado em engenharia.'
      );
    }
    const metaKw = document.querySelector('meta[name="keywords"]');
    if (metaKw) {
      metaKw.setAttribute(
        'content',
        'blog AVCB, blog CLCB, segurança do trabalho Sorocaba, PGR Campinas, LTCAT, perícia técnica engenharia'
      );
    }
    const ogUrl = document.querySelector('meta[property="og:url"]') as HTMLMetaElement | null;
    if (ogUrl) ogUrl.setAttribute('content', `${import.meta.env.VITE_SITE_URL}/blog`);
    const ogTitle = document.querySelector('meta[property="og:title"]') as HTMLMetaElement | null;
    if (ogTitle) ogTitle.setAttribute('content', 'Blog Técnico Engeporto | AVCB, CLCB e Segurança do Trabalho — Sorocaba e Campinas');
    const ogDesc = document.querySelector('meta[property="og:description"]') as HTMLMetaElement | null;
    if (ogDesc) ogDesc.setAttribute('content', 'Artigos especializados sobre AVCB, CLCB, PGR, LTCAT, perícias e segurança do trabalho em Sorocaba e Campinas.');

    // Canonical
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL}/blog`);
    return () => { canonical?.remove(); };
  }, []);

  const featuredPost = useMemo(
    () => blogPosts.find((p) => p.featured) ?? blogPosts[0],
    []
  );

  const filteredPosts = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    return blogPosts
      .filter((p) => !p.featured)
      .filter((p) =>
        selectedCategory === 'Todos' ? true : p.category === selectedCategory
      )
      .filter((p) => {
        if (!query) return true;
        return (
          p.title.toLowerCase().includes(query) ||
          p.excerpt.toLowerCase().includes(query) ||
          p.tags.some((t) => t.toLowerCase().includes(query))
        );
      });
  }, [selectedCategory, searchQuery]);

  const totalVisible = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    return blogPosts.filter((p) => {
      const catOk =
        selectedCategory === 'Todos' ? true : p.category === selectedCategory;
      const searchOk = !query
        ? true
        : p.title.toLowerCase().includes(query) ||
          p.excerpt.toLowerCase().includes(query) ||
          p.tags.some((t) => t.toLowerCase().includes(query));
      return catOk && searchOk;
    }).length;
  }, [selectedCategory, searchQuery]);

  return (
    <div className="min-h-screen bg-white">
      {/* Schema.org JSON-LD */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: SCHEMA_BLOG }}
      />

      <Navbar />

      {/* Hero com busca */}
      <BlogHero
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        totalArticles={totalVisible}
      />

      {/* Artigo em Destaque — só aparece quando não há busca ativa */}
      {!searchQuery && selectedCategory === 'Todos' && (
        <BlogFeaturedPost post={featuredPost} />
      )}

      {/* Filtro de Categorias */}
      <section className="bg-neutral-50 border-y border-neutral-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-5">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex flex-wrap items-center gap-2">
              {categories.map((cat) => {
                const count =
                  cat.value === 'Todos'
                    ? blogPosts.length
                    : (categoryCounts[cat.value] ?? 0);
                return (
                  <button
                    key={cat.value}
                    onClick={() => setSelectedCategory(cat.value)}
                    className={`flex items-center gap-1.5 px-5 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                      selectedCategory === cat.value
                        ? 'bg-[#D4AF37] text-white shadow-md'
                        : 'bg-white text-neutral-600 hover:bg-neutral-100 border border-neutral-200'
                    }`}
                  >
                    {cat.label}
                    <span
                      className={`text-xs px-1.5 py-0.5 rounded-full font-semibold ${
                        selectedCategory === cat.value
                          ? 'bg-white/25 text-white'
                          : 'bg-neutral-100 text-neutral-500'
                      }`}
                    >
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>

            <p className="text-sm text-neutral-500 whitespace-nowrap">
              <strong className="text-neutral-800">{totalVisible}</strong>{' '}
              {totalVisible === 1 ? 'artigo' : 'artigos'}
            </p>
          </div>
        </div>
      </section>

      {/* Grid de Artigos */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {/* Título da seção */}
          {!searchQuery && (
            <div className="flex items-center gap-3 mb-10">
              <span className="w-8 h-0.5 bg-[#D4AF37]" />
              <span className="text-sm font-semibold text-[#D4AF37] uppercase tracking-widest">
                {selectedCategory === 'Todos' ? 'Todos os Artigos' : selectedCategory}
              </span>
            </div>
          )}

          {searchQuery && (
            <div className="mb-8 flex items-center gap-3">
              <i className="ri-search-line text-[#D4AF37] text-lg" />
              <p className="text-sm text-neutral-600">
                Resultados para{' '}
                <strong className="text-neutral-900">&quot;{searchQuery}&quot;</strong>
              </p>
              <button
                onClick={() => setSearchQuery('')}
                className="ml-2 text-xs text-neutral-400 hover:text-red-500 transition-colors cursor-pointer flex items-center gap-1"
              >
                <i className="ri-close-line" /> Limpar busca
              </button>
            </div>
          )}

          <BlogGrid posts={filteredPosts} />
        </div>
      </section>

      {/* CTA Final */}
      <section className="relative bg-[#0A1628] text-white py-20 overflow-hidden">
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: 'radial-gradient(circle, #D4AF37 1px, transparent 1px)',
            backgroundSize: '30px 30px',
          }}
        />
        <div className="relative max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block px-4 py-1.5 bg-[#D4AF37]/20 text-[#D4AF37] text-sm font-medium rounded-full mb-5">
              Precisa de Orientação Técnica?
            </span>
            <h2 className="text-4xl font-bold mb-4 font-['Poppins']">
              Fale com um Especialista
            </h2>
            <p className="text-lg text-neutral-300 mb-8 max-w-2xl mx-auto">
              Nossa equipe está pronta para esclarecer suas dúvidas sobre <strong className="text-white">AVCB</strong>, <strong className="text-white">CLCB</strong> e segurança do trabalho em Sorocaba e Campinas.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4">
              <a
                href={`https://wa.me/5515996719308?text=${encodeURIComponent('Olá! Li um artigo no blog e gostaria de solicitar um orçamento.')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#0A1628] px-8 py-4 rounded-lg font-semibold hover:bg-[#C4A137] transition-all shadow-xl whitespace-nowrap cursor-pointer"
              >
                <i className="ri-whatsapp-line text-lg" />
                Solicitar Orçamento
              </a>
              <a
                href="tel:+5515996719308"
                className="inline-flex items-center gap-2 border border-white/30 text-white px-8 py-4 rounded-lg font-semibold hover:bg-white/10 transition-all whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line text-lg" />
                (15) 99671-9308
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
      <ScrollToTopButton />
    </div>
  );
};

export default BlogPage;
